'use client';

import React, { createContext, useContext, useMemo, type ReactNode } from 'react';

/** Trefferart, wie sie die Suchmaschine liefert.
 *  EXACT   = Token steht 1:1 im Suchbegriff
 *  SIMILAR = über Typo-Toleranz, Teilwort oder Synonym gefunden
 *            ("PU-Schaum" findet "PU-Klebeschaum") */
export type SearchMatchKind = 'EXACT' | 'SIMILAR';

export interface SearchMatchToken {
  token: string;
  kind: SearchMatchKind;
}

interface SearchHighlightValue {
  searchTerm: string;
  matches: readonly SearchMatchToken[];
}

const NO_MATCHES: readonly SearchMatchToken[] = [];

const SearchHighlightContext = createContext<SearchHighlightValue>({
  searchTerm: '',
  matches: NO_MATCHES,
});

interface SearchHighlightProviderProps {
  searchTerm: string;
  /** Von der Suchmaschine tatsächlich getroffene Tokens (DocumentConnection.
   *  searchMatches). Fehlt sie (Postgres-Fallback), wird wie bisher nur der
   *  wörtliche Suchbegriff markiert. */
  matches?: readonly SearchMatchToken[] | null;
  children: ReactNode;
}

export function SearchHighlightProvider({
  searchTerm,
  matches,
  children,
}: SearchHighlightProviderProps) {
  const value = useMemo<SearchHighlightValue>(
    () => ({ searchTerm, matches: matches ?? NO_MATCHES }),
    [searchTerm, matches],
  );

  return (
    <SearchHighlightContext.Provider value={value}>
      {children}
    </SearchHighlightContext.Provider>
  );
}

export function useSearchHighlight(): string {
  return useContext(SearchHighlightContext).searchTerm;
}

export function useSearchMatches(): readonly SearchMatchToken[] {
  return useContext(SearchHighlightContext).matches;
}

interface HighlightTextProps {
  text: string | null | undefined;
  className?: string;
}

const escapeRegExp = (input: string): string =>
  input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Farben kommen aus der Tailwind-Rampe, die global.css im Dark Mode
 *  invertiert — deshalb nur remappte Töne (yellow/sky/gray), keine
 *  Roh-Hexwerte und keine dark:-Sonderfälle. */
export const MARK_CLASS: Record<SearchMatchKind, string> = {
  EXACT: 'bg-yellow-200 text-gray-900 rounded-sm px-0.5',
  SIMILAR:
    'bg-sky-100 text-gray-900 rounded-sm px-0.5 underline decoration-dotted decoration-sky-600 underline-offset-2',
};

const NO_SEGMENTS = { segments: null, kinds: null } as const;

export function HighlightText({ text, className }: HighlightTextProps) {
  const { searchTerm, matches } = useContext(SearchHighlightContext);
  const term = searchTerm.trim();

  const { segments, kinds } = useMemo(() => {
    if (!text) return NO_SEGMENTS;

    const kinds = new Map<string, SearchMatchKind>();
    // Wörtlich getippte Tokens bleiben immer der exakte Treffer.
    for (const token of term.split(/\s+/)) {
      if (token.length > 0) kinds.set(token.toLowerCase(), 'EXACT');
    }
    for (const match of matches) {
      const token = match.token.trim().toLowerCase();
      if (token.length === 0 || kinds.get(token) === 'EXACT') continue;
      kinds.set(token, match.kind);
    }
    if (kinds.size === 0) return NO_SEGMENTS;

    // Längste Alternative zuerst, sonst zerlegt "schaum" das Wort
    // "Klebeschaum", bevor "klebeschaum" greifen kann.
    const alternatives = [...kinds.keys()].sort((a, b) => b.length - a.length);
    const pattern = new RegExp(
      `(${alternatives.map(escapeRegExp).join('|')})`,
      'ig',
    );
    const parts = text.split(pattern);
    if (parts.length === 1) return NO_SEGMENTS;
    return { segments: parts, kinds };
  }, [text, term, matches]);

  if (!text) return null;

  if (!segments || !kinds) {
    return className ? <span className={className}>{text}</span> : <>{text}</>;
  }

  const content = segments.map((part, idx) => {
    const kind = kinds.get(part.toLowerCase());
    if (!kind) return <React.Fragment key={idx}>{part}</React.Fragment>;
    return (
      <mark
        key={idx}
        className={MARK_CLASS[kind]}
        title={
          kind === 'SIMILAR' && term
            ? `Ähnlicher Treffer zu „${term}“`
            : undefined
        }
      >
        {part}
      </mark>
    );
  });

  return className ? <span className={className}>{content}</span> : <>{content}</>;
}
